/**
 * Popup Manager - Alpine.js Component
 * 
 * Handles popup display, triggers, frequency management,
 * and cart condition validation.
 * 
 * Dependencies:
 * - Alpine.js
 * - js-cookie (for cookie management)
 * 
 * Data Flow:
 * 1. PHP passes popup configurations via globberPopups object
 * 2. This script initializes popups based on their triggers
 * 3. Validates frequency/cart conditions before showing
 * 4. Manages show/hide animations and user interactions
 * 5. Pushes events to dataLayer for GTM tracking
 * 
 * @package Globber
 * @since 2.0.0
 */

(function() {
    'use strict';
    
    // Immediate debug - confirms script is loaded
    console.log('%c[Popup Manager] Script loaded', 'color: #22c55e; font-weight: bold;');
    console.log('%c[Popup Manager] globberPopups available:', 'color: #22c55e;', typeof globberPopups !== 'undefined');
    if (typeof globberPopups !== 'undefined') {
        console.log('%c[Popup Manager] Debug mode:', 'color: #22c55e;', globberPopups.debug);
        console.log('%c[Popup Manager] Total popups from server:', 'color: #22c55e;', globberPopups.totalFound);
    }

    /**
     * Debug mode - enabled when WP_DEBUG is true
     */
    const DEBUG = typeof globberPopups !== 'undefined' && globberPopups.debug === true;

    /**
     * Debug logger - outputs to console when WP_DEBUG is enabled
     */
    const log = DEBUG
        ? (...args) => console.log('%c[Popup Manager]', 'color: #9333ea; font-weight: bold;', ...args)
        : () => {};

    const warn = DEBUG
        ? (...args) => console.warn('%c[Popup Manager]', 'color: #f59e0b; font-weight: bold;', ...args)
        : () => {};

    const error = DEBUG
        ? (...args) => console.error('%c[Popup Manager]', 'color: #ef4444; font-weight: bold;', ...args)
        : () => {};

    // Always log initialization status for debugging
    if (DEBUG) {
        console.group('%c[Popup Manager] Initialization', 'color: #9333ea; font-weight: bold;');
        console.log('globberPopups object:', typeof globberPopups !== 'undefined' ? globberPopups : 'NOT DEFINED');
    }

    // Check if globberPopups exists
    if (typeof globberPopups === 'undefined') {
        if (DEBUG) {
            error('globberPopups is undefined - PHP localization failed or no popups configured');
            console.groupEnd();
        }
        return;
    }

    // Log debug info from server
    if (DEBUG && globberPopups.debugInfo) {
        console.log('Server Debug Info:', globberPopups.debugInfo);
    }

    // Check if there are popups or an ATC popup configured.
    // Must mirror the PHP condition in globber_render_popup_html():
    //   if (empty($popups) && !$atc_config) return;
    // If either exists the HTML is rendered and the Alpine component must be registered.
    const hasPopups    = globberPopups.popups && globberPopups.popups.length > 0;
    const hasAtcPopup  = !!globberPopups.atcPopup;

    if (!hasPopups && !hasAtcPopup) {
        if (DEBUG) {
            warn('No popups or ATC popup found. Total from server:', globberPopups.totalFound || 0);
            warn('This could mean:');
            warn('  1. No popups are set to "Active" in WP Admin');
            warn('  2. All popups failed server-side targeting (page/user/schedule)');
            warn('  3. The popup post type query returned no results');
            warn('  4. ATC popup is not configured in Popup Settings');
            console.groupEnd();
        }
        return;
    }

    if (!hasPopups && DEBUG) {
        warn('No standard popups, but ATC popup is configured — registering Alpine component.');
    }

    if (DEBUG) {
        console.log(`Found ${globberPopups.popups.length} popup(s) that passed server-side targeting:`);
        globberPopups.popups.forEach((p, i) => {
            console.log(`  ${i + 1}. ID: ${p.id}, Title: "${p.title}", Template: ${p.template}, Trigger: ${p.trigger?.type}`);
        });
        
        // Check Alpine.js availability
        if (typeof Alpine === 'undefined') {
            console.log('⚠ Alpine.js not yet loaded - will register on alpine:init event');
        } else {
            console.log('✓ Alpine.js is available');
        }
        console.groupEnd();
    }

    /**
     * Cookie/Storage Keys
     */
    const COOKIE_PREFIX = 'globber_popup_';
    const STORAGE_PREFIX = 'globber_popup_';

    /**
     * Push event to dataLayer for GTM tracking
     */
    function pushToDataLayer(eventName, eventData = {}) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: eventName,
            popup: eventData
        });
        log('DataLayer push:', eventName, eventData);
    }

    /**
     * Check if popup should show based on frequency rules
     */
    function checkFrequency(popup) {
        const frequency = popup.frequency || 'once';
        const popupId = popup.id;
        const cookieKey = COOKIE_PREFIX + popupId;
        const storageKey = STORAGE_PREFIX + popupId;

        // Check max displays
        if (popup.maxDisplays > 0) {
            const displayCount = parseInt(localStorage.getItem(storageKey + '_count') || '0', 10);
            if (displayCount >= popup.maxDisplays) {
                log(`Popup ${popupId} reached max displays (${popup.maxDisplays})`);
                return false;
            }
        }

        switch (frequency) {
            case 'always':
                return true;

            case 'once':
                // Check cookie
                if (typeof Cookies !== 'undefined' && Cookies.get(cookieKey)) {
                    log(`Popup ${popupId} already shown (cookie)`);
                    return false;
                }
                return true;

            case 'once-per-session':
                // Check sessionStorage
                if (sessionStorage.getItem(storageKey)) {
                    log(`Popup ${popupId} already shown this session`);
                    return false;
                }
                return true;

            case 'once-per-day':
            case 'once-per-week':
                // Check cookie with appropriate expiry
                if (typeof Cookies !== 'undefined' && Cookies.get(cookieKey)) {
                    log(`Popup ${popupId} already shown within frequency period`);
                    return false;
                }
                return true;

            default:
                return true;
        }
    }

    /**
     * Mark popup as shown for frequency tracking
     */
    function markAsShown(popup) {
        const frequency = popup.frequency || 'once';
        const popupId = popup.id;
        const cookieKey = COOKIE_PREFIX + popupId;
        const storageKey = STORAGE_PREFIX + popupId;

        // Increment display count
        if (popup.maxDisplays > 0) {
            const displayCount = parseInt(localStorage.getItem(storageKey + '_count') || '0', 10);
            localStorage.setItem(storageKey + '_count', displayCount + 1);
        }

        switch (frequency) {
            case 'once':
                if (typeof Cookies !== 'undefined') {
                    Cookies.set(cookieKey, '1', { 
                        expires: popup.cookieExpiry || 30,
                        sameSite: 'Lax'
                    });
                }
                break;

            case 'once-per-session':
                sessionStorage.setItem(storageKey, '1');
                break;

            case 'once-per-day':
                if (typeof Cookies !== 'undefined') {
                    Cookies.set(cookieKey, '1', { expires: 1, sameSite: 'Lax' });
                }
                break;

            case 'once-per-week':
                if (typeof Cookies !== 'undefined') {
                    Cookies.set(cookieKey, '1', { expires: 7, sameSite: 'Lax' });
                }
                break;
        }
    }

    /**
     * Check cart conditions via AJAX
     */
    async function checkCartConditions(popup) {
        if (!popup.hasCartConditions || !popup.cartRules || !popup.cartRules.length) {
            return true;
        }

        try {
            const formData = new FormData();
            formData.append('action', 'globber_check_cart_conditions');
            formData.append('nonce', globberPopups.nonce);
            formData.append('rules', JSON.stringify(popup.cartRules));

            const response = await fetch(globberPopups.ajaxUrl, {
                method: 'POST',
                body: formData,
                credentials: 'same-origin'
            });

            const data = await response.json();
            return data.success && data.data.passes;
        } catch (error) {
            log('Cart condition check failed:', error);
            return true; // Show popup if check fails
        }
    }

    /**
     * Alpine.js Popup Manager Component
     */
    document.addEventListener('alpine:init', () => {
        log('Alpine.js alpine:init event fired - registering popupManager component');
        
        Alpine.data('popupManager', () => ({
            popups: globberPopups.popups || [],
            activePopups: {},
            atcPopupActive: false,
            atcPopupConfig: globberPopups.atcPopup || null,
            pageLoadTime: Date.now(),
            exitIntentEnabled: {},
            scrollTriggered: {},
            timers: {},

            /**
             * Free-shipping progress bar state.
             *
             * Bootstrapped from the PHP-localised config on first render, then
             * kept live by fetchFreeShippingData() which is called on every
             * add-to-cart / cart-update event.
             */
            freeShipping: {
                enabled:            !!(globberPopups.atcPopup?.freeShipping?.enabled),
                threshold:          globberPopups.atcPopup?.freeShipping?.threshold          ?? 0,
                cartTotal:          globberPopups.atcPopup?.freeShipping?.cartTotal          ?? 0,
                remaining:          globberPopups.atcPopup?.freeShipping?.remaining          ?? 0,
                formattedRemaining: globberPopups.atcPopup?.freeShipping?.formattedRemaining ?? '',
                formattedThreshold: globberPopups.atcPopup?.freeShipping?.formattedThreshold ?? '',
                reached:            !!(globberPopups.atcPopup?.freeShipping?.reached),
                progressPct:        0,
                message:            globberPopups.atcPopup?.freeShipping?.message            ?? '',
                reachedMessage:     globberPopups.atcPopup?.freeShipping?.reachedMessage     ?? '',
            },
            
            /**
             * Initialize the popup manager
             */
            init() {
                if (DEBUG) {
                    console.group('%c[Popup Manager] Alpine Component Init', 'color: #9333ea; font-weight: bold;');
                    console.log('Popups to initialize:', this.popups.length);
                    console.log('Cookies library available:', typeof Cookies !== 'undefined');
                    console.log('jQuery available:', typeof jQuery !== 'undefined');
                }
                
                // Start server-side validation (catches stale cached pages)
                this._validationPromise = this._validatePopups();

                this.popups.forEach(popup => {
                    this.activePopups[popup.id] = false;
                    this.setupTrigger(popup);
                });

                // Set up the add-to-cart popup if configured
                if (this.atcPopupConfig) {
                    this.setupAddToCartTrigger();

                    // Initialise free-shipping progress bar from server data
                    if (this.freeShipping.enabled && this.freeShipping.threshold > 0) {
                        this._computeFreeShippingProgress();
                    }

                    // Always refresh from AJAX once so stale/localized threshold=0
                    // can recover to the latest server-side config.
                    if (this.freeShipping.enabled) {
                        this.fetchFreeShippingData();
                    }
                }

                // Handle GlobberPopupManager.show(popupId) external API calls
                document.addEventListener('globber:popup:trigger', (e) => {
                    const popupId = e.detail?.popupId;
                    const popup = this.popups.find(p => p.id === popupId);
                    if (popup) {
                        this.show(popup);
                    } else if (popupId === 'atc' && this.atcPopupConfig) {
                        this.showAtc();
                    }
                });

                // Global exit intent listener (mouseleave on documentElement for cross-browser support)
                document.documentElement.addEventListener('mouseleave', (e) => this.handleExitIntent(e));
                
                // Listen for cart updates (WooCommerce)
                document.body.addEventListener('updated_cart_totals', () => {
                    this.recheckCartConditions();
                    this.fetchFreeShippingData();
                });
                
                // Handle cart fragments refresh
                if (typeof jQuery !== 'undefined') {
                    jQuery(document.body).on('wc_fragments_refreshed', () => {
                        this.recheckCartConditions();
                        this.fetchFreeShippingData();
                    });
                }
                
                if (DEBUG) {
                    console.log('✓ Popup manager initialized successfully');
                    console.groupEnd();
                }
            },

            /**
             * Setup trigger for a popup
             */
            setupTrigger(popup) {
                const trigger = popup.trigger;
                
                if (DEBUG) {
                    console.log(`Setting up trigger for popup ${popup.id} (${popup.title}):`, trigger);
                }
                
                if (!trigger || !trigger.type) {
                    warn(`Popup ${popup.id} has no trigger type defined`);
                    return;
                }
                
                switch (trigger.type) {
                    case 'timer':
                        this.setupTimerTrigger(popup);
                        break;
                    case 'exit-intent':
                        this.setupExitIntentTrigger(popup);
                        break;
                    case 'element-click':
                        this.setupClickTrigger(popup);
                        break;
                    case 'scroll-percent':
                        this.setupScrollTrigger(popup);
                        break;
                }
            },

            /**
             * Setup timer-based trigger
             */
            setupTimerTrigger(popup) {
                const delay = (popup.trigger.delay || 3) * 1000;
                
                this.timers[popup.id] = setTimeout(async () => {
                    await this.attemptShow(popup);
                }, delay);
                
                log(`Timer trigger set for popup ${popup.id}: ${delay}ms`);
            },

            /**
             * Setup exit intent trigger
             */
            setupExitIntentTrigger(popup) {
                const minDelay = (popup.trigger.exitDelay || 5) * 1000;
                
                // Enable exit intent after minimum delay
                setTimeout(() => {
                    this.exitIntentEnabled[popup.id] = popup;
                    log(`Exit intent enabled for popup ${popup.id}`);
                }, minDelay);
            },

            /**
             * Handle exit intent detection
             */
            async handleExitIntent(e) {
                // Only trigger when mouse leaves toward top of viewport
                if (e.clientY > 50) return;
                if (!e.relatedTarget) {
                    // Check all exit-intent popups
                    for (const popupId in this.exitIntentEnabled) {
                        const popup = this.exitIntentEnabled[popupId];
                        if (popup && !this.activePopups[popup.id]) {
                            const sensitivity = popup.trigger.exitSensitivity || 'medium';
                            let threshold = 20;
                            
                            switch (sensitivity) {
                                case 'low': threshold = 0; break;
                                case 'medium': threshold = 20; break;
                                case 'high': threshold = 50; break;
                            }
                            
                            if (e.clientY <= threshold) {
                                await this.attemptShow(popup);
                                delete this.exitIntentEnabled[popupId];
                                break; // Only show one popup per exit intent
                            }
                        }
                    }
                }
            },

            /**
             * Setup click-based trigger
             */
            setupClickTrigger(popup) {
                const selector = popup.trigger.clickSelector;
                if (!selector) return;
                
                const handler = async (e) => {
                    const trigger = e.target.closest(selector);
                    if (!trigger) return;
                    
                    e.preventDefault();
                    await this.attemptShow(popup);
                };
                
                document.addEventListener('click', handler);
                this._clickHandlers = this._clickHandlers || [];
                this._clickHandlers.push(handler);
                
                log(`Click trigger set for popup ${popup.id}: ${selector}`);
            },

            /**
             * Setup scroll percentage trigger
             */
            setupScrollTrigger(popup) {
                const percentage = popup.trigger.scrollPercent || 50;
                
                const checkScroll = () => {
                    if (this.scrollTriggered[popup.id]) return;
                    
                    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
                    const scrollPercent = (window.scrollY / scrollHeight) * 100;
                    
                    if (scrollPercent >= percentage) {
                        this.scrollTriggered[popup.id] = true;
                        this.attemptShow(popup);
                        window.removeEventListener('scroll', checkScroll);
                    }
                };
                
                window.addEventListener('scroll', checkScroll, { passive: true });
                log(`Scroll trigger set for popup ${popup.id}: ${percentage}%`);
            },

            /**
             * Setup add-to-cart trigger for the ATC mini-cart popup.
             *
             * - jQuery added_to_cart event  → attemptShowAtc() (frequency-gated)
             * - #shopping-cart-link click    → showAtc() (always, intentional action)
             */
            setupAddToCartTrigger() {
                if (typeof jQuery === 'undefined') {
                    warn('jQuery not available — add-to-cart trigger cannot be set up');
                    return;
                }

                // WooCommerce fires this on the body after every successful add-to-cart
                jQuery(document.body).on('added_to_cart.globber_atc', () => {
                    this.attemptShowAtc();
                    this.fetchFreeShippingData();
                });

                // Cart icon — bypass frequency (intentional user action)
                const cartIcon = document.querySelector('#shopping-cart-link');
                if (cartIcon) {
                    cartIcon.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.showAtc();
                    });
                }

                log('Add-to-cart trigger set up');
            },

            /**
             * Attempt to show the ATC popup (checks frequency)
             */
            async attemptShowAtc() {
                const fakePopup = { id: 'atc', ...this.atcPopupConfig };

                if (this.atcPopupActive) {
                    log('ATC popup already active — skipping');
                    return;
                }

                if (!checkFrequency(fakePopup)) {
                    log('ATC popup blocked by frequency setting');
                    return;
                }

                this.showAtc();
            },

            /**
             * Show the ATC mini-cart popup
             */
            showAtc() {
                this.atcPopupActive = true;
                markAsShown({ id: 'atc', ...this.atcPopupConfig });

                pushToDataLayer('popup_view', {
                    popup_id: 'atc',
                    popup_title: 'Add to Cart Popup',
                    template: this.atcPopupConfig.template,
                    trigger_type: 'add-to-cart'
                });

                log('Showing ATC mini-cart popup');

                document.dispatchEvent(new CustomEvent('globber:popup:show', {
                    detail: { popup: { id: 'atc' } }
                }));
            },

            /**
             * Close the ATC mini-cart popup
             */
            closeAtc() {
                this.atcPopupActive = false;

                pushToDataLayer('popup_close', {
                    popup_id: 'atc',
                    popup_title: 'Add to Cart Popup',
                    template: this.atcPopupConfig?.template
                });

                log('Closed ATC mini-cart popup');

                document.dispatchEvent(new CustomEvent('globber:popup:close', {
                    detail: { popupId: 'atc' }
                }));
            },

            /**
             * Validate popup IDs with the server.
             * 
             * Guards against stale full-page / browser cache: the server
             * returns only the IDs whose "Active" toggle (switcher) is ON.
             * Any popup not in the response is purged from the runtime list
             * and its triggers are cleaned up.
             *
             * @return {Promise<void>}
             */
            async _validatePopups() {
                try {
                    const ids = this.popups.map(p => p.id);
                    const formData = new FormData();
                    formData.append('action', 'globber_validate_popup_ids');
                    formData.append('nonce', globberPopups.nonce);
                    formData.append('popup_ids', JSON.stringify(ids));

                    const res  = await fetch(globberPopups.ajaxUrl, {
                        method: 'POST',
                        body: formData,
                        credentials: 'same-origin'
                    });

                    const json = await res.json();

                    if (json.success) {
                        const activeIds = json.data.activeIds.map(Number);
                        const stale     = this.popups.filter(p => !activeIds.includes(p.id));

                        if (stale.length) {
                            stale.forEach(p => {
                                warn(`Popup ${p.id} ("${p.title}") is no longer active — removing`);
                                // Clear timer
                                if (this.timers[p.id]) {
                                    clearTimeout(this.timers[p.id]);
                                    delete this.timers[p.id];
                                }
                                // Remove exit-intent entry
                                delete this.exitIntentEnabled[p.id];
                            });

                            this.popups = this.popups.filter(p => activeIds.includes(p.id));
                            log('Popups after validation:', this.popups.map(p => p.id));
                        } else {
                            log('All popups validated — none were stale');
                        }
                    }
                } catch (err) {
                    // Network error — fail open so popups still work offline / on dev
                    log('Popup validation request failed, keeping current config:', err);
                }
            },

            /**
             * Attempt to show a popup (checks all conditions)
             */
            async attemptShow(popup) {
                // Wait for server-side active/inactive validation
                if (this._validationPromise) {
                    await this._validationPromise;
                }

                // After validation, check the popup is still in the list
                if (!this.popups.find(p => p.id === popup.id)) {
                    warn(`Popup ${popup.id} was removed by validation — skipping`);
                    return;
                }

                if (DEBUG) {
                    console.group(`%c[Popup Manager] Attempting to show popup ${popup.id}`, 'color: #9333ea; font-weight: bold;');
                    console.log('Popup config:', popup);
                }
                
                // Check if already showing
                if (this.activePopups[popup.id]) {
                    if (DEBUG) {
                        warn('✗ BLOCKED: Popup already active');
                        console.groupEnd();
                    }
                    return;
                }
                log('✓ Not already active');
                
                // Check frequency
                if (!checkFrequency(popup)) {
                    if (DEBUG) {
                        warn(`✗ BLOCKED: Frequency check failed (${popup.frequency})`);
                        console.log('Cookie key:', COOKIE_PREFIX + popup.id);
                        console.log('Cookie value:', typeof Cookies !== 'undefined' ? Cookies.get(COOKIE_PREFIX + popup.id) : 'Cookies not available');
                        console.log('Session storage:', sessionStorage.getItem(STORAGE_PREFIX + popup.id));
                        console.groupEnd();
                    }
                    return;
                }
                log('✓ Frequency check passed');
                
                // Check cart conditions (async)
                if (popup.hasCartConditions) {
                    log('Checking cart conditions via AJAX...');
                    const cartPasses = await checkCartConditions(popup);
                    if (!cartPasses) {
                        if (DEBUG) {
                            warn('✗ BLOCKED: Cart conditions not met');
                            console.log('Cart rules:', popup.cartRules);
                            console.groupEnd();
                        }
                        return;
                    }
                    log('✓ Cart conditions passed');
                } else {
                    log('✓ No cart conditions to check');
                }
                
                // Show the popup
                if (DEBUG) {
                    console.log('✓ ALL CHECKS PASSED - Showing popup!');
                    console.groupEnd();
                }
                this.show(popup);
            },

            /**
             * Show a popup
             */
            show(popup) {
                this.activePopups[popup.id] = true;
                markAsShown(popup);
                
                // Push to dataLayer
                pushToDataLayer('popup_view', {
                    popup_id: popup.id,
                    popup_title: popup.title,
                    template: popup.template,
                    trigger_type: popup.trigger.type
                });
                
                log(`Showing popup ${popup.id}`);
                
                // Dispatch custom event
                document.dispatchEvent(new CustomEvent('globber:popup:show', {
                    detail: { popup }
                }));
            },

            /**
             * Close a popup
             */
            close(popupId) {
                this.activePopups[popupId] = false;
                
                const popup = this.popups.find(p => p.id === popupId);
                if (popup) {
                    pushToDataLayer('popup_close', {
                        popup_id: popup.id,
                        popup_title: popup.title,
                        template: popup.template
                    });
                }
                
                log(`Closed popup ${popupId}`);
                
                // Dispatch custom event
                document.dispatchEvent(new CustomEvent('globber:popup:close', {
                    detail: { popupId }
                }));
            },

            /**
             * Handle CTA click
             */
            handleCtaClick(popup) {
                pushToDataLayer('popup_cta_click', {
                    popup_id: popup.id,
                    popup_title: popup.title,
                    cta_url: popup.cta?.url || '',
                    cta_text: popup.cta?.title || ''
                });
            },

            /**
             * Compute free shipping progress percentage from current state.
             * Called locally after state updates to avoid redundant AJAX calls.
             */
            _computeFreeShippingProgress() {
                const fs = this.freeShipping;
                if (fs.threshold > 0) {
                    fs.progressPct = Math.min(100, Math.round((fs.cartTotal / fs.threshold) * 100));
                } else {
                    fs.progressPct = 0;
                }
            },

            /**
             * Fetch live free-shipping data from the server after a cart update.
             *
             * Updates this.freeShipping so Alpine's reactive bindings automatically
             * reflect the latest cart total and remaining amount.
             */
            async fetchFreeShippingData() {
                if (!this.freeShipping.enabled) {
                    // --- FREE SHIPPING DEBUG (remove once issue diagnosed) ---
                    console.warn('[Free Shipping Bar] fetchFreeShippingData skipped — enabled:', this.freeShipping.enabled, '/ threshold:', this.freeShipping.threshold);
                    // --- END FREE SHIPPING DEBUG ---
                    return;
                }

                try {
                    const formData = new FormData();
                    formData.append('action', 'globber_get_free_shipping_data');
                    formData.append('nonce', globberPopups.nonce);

                    const res  = await fetch(globberPopups.ajaxUrl, {
                        method: 'POST',
                        body: formData,
                        credentials: 'same-origin'
                    });

                    const json = await res.json();

                    if (json.success && json.data) {
                        const d = json.data;
                        this.freeShipping.threshold          = d.threshold;
                        this.freeShipping.cartTotal          = d.cartTotal;
                        this.freeShipping.remaining          = d.remaining;
                        this.freeShipping.formattedRemaining = d.formattedRemaining;
                        this.freeShipping.formattedThreshold = d.formattedThreshold;
                        this.freeShipping.reached            = d.reached;
                        this._computeFreeShippingProgress();
                        log('Free shipping data refreshed:', d);
                    }
                } catch (err) {
                    log('fetchFreeShippingData failed (non-critical):', err);
                }
            },

            /**
             * Re-check cart conditions for all popups with cart rules
             */
            async recheckCartConditions() {
                log('Re-checking cart conditions after cart update');
                
                for (const popup of this.popups) {
                    if (popup.hasCartConditions && 
                        popup.trigger.type === 'timer' && 
                        !this.activePopups[popup.id] &&
                        checkFrequency(popup)) {
                        
                        const cartPasses = await checkCartConditions(popup);
                        if (cartPasses) {
                            this.show(popup);
                        }
                    }
                }
            },

            /**
             * Cleanup on destroy
             */
            destroy() {
                // Clear all timers
                for (const timerId in this.timers) {
                    clearTimeout(this.timers[timerId]);
                }
                
                // Remove click handlers
                if (this._clickHandlers) {
                    this._clickHandlers.forEach(handler => {
                        document.removeEventListener('click', handler);
                    });
                    this._clickHandlers = [];
                }

                // Remove add-to-cart jQuery listener
                if (typeof jQuery !== 'undefined') {
                    jQuery(document.body).off('.globber_atc');
                }
            }
        }));
    });

    /**
     * Expose popup manager API globally
     */
    window.GlobberPopupManager = {
        /**
         * Manually trigger a popup by ID
         */
        show(popupId) {
            document.dispatchEvent(new CustomEvent('globber:popup:trigger', {
                detail: { popupId }
            }));
        },

        /**
         * Close a popup by ID
         */
        close(popupId) {
            document.dispatchEvent(new CustomEvent('globber:popup:close', {
                detail: { popupId }
            }));
        },

        /**
         * Reset frequency tracking for a popup
         */
        resetFrequency(popupId) {
            const cookieKey = COOKIE_PREFIX + popupId;
            const storageKey = STORAGE_PREFIX + popupId;
            
            if (typeof Cookies !== 'undefined') {
                Cookies.remove(cookieKey);
            }
            sessionStorage.removeItem(storageKey);
            localStorage.removeItem(storageKey + '_count');
            
            log(`Frequency reset for popup ${popupId}`);
        }
    };

})();
